// Geometria da pilha de toasts — puro, sem React e sem DOM.
//
// Mora fora do provider porque é a única parte com aritmética de verdade: o
// resto é ciclo de vida. Separado, dá para provar o cálculo com alturas
// desiguais sem montar componente nem simular `ResizeObserver`.
//
// Convenção que atravessa o arquivo inteiro: **índice 0 é o toast da FRENTE, o
// mais novo**. A lista chega em ordem de inserção (mais antigo primeiro), então
// o índice é contado de trás para frente. O mais recente é o que precisa estar
// legível; os anteriores encolhem atrás dele.
import { TOAST_STACK_GAP, TOAST_STACK_PEEK } from '@/config/toast';

export interface StackSlot {
  id: string;
  /** 0 = frente (mais novo). Alimenta escala e z-index. */
  index: number;
  /** Deslocamento vertical em px quando a pilha está expandida. */
  offset: number;
}

export interface StackGeometry {
  slots: StackSlot[];
  /** Altura da região com a pilha em repouso. */
  collapsedHeight: number;
  /** Altura da região com a pilha aberta — o mouse precisa caber nela. */
  expandedHeight: number;
  /**
   * Altura do toast da frente. Em repouso ela é imposta a TODOS: um toast de
   * trás mais baixo que o da frente ficaria inteiramente escondido atrás dele, e
   * a pilha pareceria ter um toast só. Uniformizando, a beirada de cada um
   * aparece embaixo — que é o sinal de "tem mais coisa aqui".
   */
  frontHeight: number;
}

/**
 * Distribui posições a partir das alturas medidas.
 *
 * `heights` costuma vir incompleto: o `ResizeObserver` só reporta depois da
 * primeira pintura, então o primeiro render de um toast novo não tem altura
 * nenhuma. Tratamos ausência como zero em vez de adiar a renderização — o
 * toast aparece na posição certa no frame seguinte, e a `transition` cobre a
 * correção. Adiar produziria um salto bem mais visível.
 */
export function computeStackGeometry(
  ids: readonly string[],
  heights: Readonly<Record<string, number>>,
): StackGeometry {
  const total = ids.length;
  if (total === 0) {
    return { slots: [], collapsedHeight: 0, expandedHeight: 0, frontHeight: 0 };
  }

  // Da frente para trás: o toast da frente é o último inserido.
  const frontFirst = [...ids].reverse();

  let running = 0;
  const slots = frontFirst.map((id, index) => {
    // O offset de um toast é o espaço ocupado por todos os que estão à frente
    // dele — por isso acumula ANTES de somar a própria altura.
    const slot: StackSlot = { id, index, offset: running };
    running += (heights[id] ?? 0) + TOAST_STACK_GAP;
    return slot;
  });

  const frontHeight = heights[frontFirst[0]] ?? 0;

  return {
    frontHeight,
    // Colapsada mostra o da frente inteiro mais a beirada de cada um atrás.
    collapsedHeight: frontHeight + (total - 1) * TOAST_STACK_PEEK,
    // Expandida é a soma das alturas com os gaps — menos o gap sobrando que o
    // laço somou depois do último.
    expandedHeight: Math.max(0, running - TOAST_STACK_GAP),
    // De volta à ordem de inserção: o DOM segue cronológico (leitores de tela
    // anunciam na ordem em que os avisos aconteceram); quem posiciona é o CSS.
    slots: slots.reverse(),
  };
}
